import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mediaCache } from '@/shared/lib/mediaCache';
import { useIsMobileViewport } from '@/shared/hooks/useIsMobileViewport';
import { useScheduledMidias } from '@/shared/hooks/useScheduledMidias';
import { usePlayerConfig } from '../hooks/usePlayerConfig';
import { usePlayerInfra } from '../hooks/usePlayerInfra';
import { usePlayerMediaSource } from '../hooks/usePlayerMediaSource';
import { usePlayerLogos } from '../hooks/usePlayerLogos';
import { usePlayerSockets } from '../hooks/usePlayerSockets';
import { useRaioXMusica } from '../hooks/useRaioXMusica';
import { useYoutubePlaylist } from '../hooks/useYoutubePlaylist';
import { useJukeboxQueue } from '../hooks/useJukeboxQueue';
import { useMomentoEspecial } from '../hooks/useMomentoEspecial';
import { useIdentidadeSonora } from '../hooks/useIdentidadeSonora';
import { useHeartbeat } from '../hooks/useHeartbeat';
import { useNotificationToasts } from '../hooks/useNotificationToasts';
import { useBlobMemoryManager } from '../hooks/useBlobMemoryManager';
import { useFutebolEventPersistence } from '../hooks/useFutebolEventPersistence';
import { useAutoHideControls } from '../hooks/useAutoHideControls';
import { useAdSidebar } from '../hooks/useAdSidebar';
import { useCampanhasPlataforma } from '../hooks/useCampanhasPlataforma';
import { ehCampanha, intercalarCampanhas, type MidiaCampanha } from '../../domain/usecases/campanhasPlayer';
import { useRegistrarExibicoes } from '../hooks/useRegistrarExibicoes';
import { useSlideshowAdvance } from '../hooks/useSlideshowAdvance';
import { usePlayerRemoteState } from '../hooks/usePlayerRemoteState';
import { usePlayerRemoteCommands } from '../hooks/usePlayerRemoteCommands';
import { useYoutubeErroEmbed } from '../hooks/useYoutubeErroEmbed';
import PlayerGuard from '../components/PlayerGuard';
import PlayerContent from '../components/PlayerContent';
import PlayerOverlays from '../components/PlayerOverlays';
import PlayerMobileGate from '../components/PlayerMobileGate';
import { type YoutubeSlideHandle } from '../components/YoutubeSlide';
import { type CaptureSlideHandle, type WindowCaptureHandle } from '../components/CaptureSlide';
import { useFutebolRealtime, useFutebolSounds } from '@/features/futebol';
import { useVotacaoAutomatica } from '@/features/futebol-votacao';
import { usePdvEngajamento } from '@/features/pdv-engajamento';
import { useResultadoEleicao } from '@/features/eleicao';
import { isGolOverlayAtivo } from '../../domain/usecases/hypeMoment';
import { resolverFaixaAtual, montarMusicaAtualBroadcast } from '../../domain/usecases/faixaAtual';
import { useTvEngajamento } from '../hooks/useTvEngajamento';
import { useHypeMoment } from '../hooks/useHypeMoment';
import { useLogo } from '@/shared/hooks/useLogo';
import { tenantHomePath } from '@/shared/hooks/useTenantHomePath';
import { useTransmissaoLive } from '@/features/transmissao';
import { useHdmiCaptura } from '../hooks/useHdmiCaptura';
import type { PlaylistItem, FilaJukebox } from '@/shared/types';

// ── Player principal ──────────────────────────────────────────────────────────

/**
 * Decide o gate mobile ANTES de montar qualquer hook da TV: com os hooks
 * rodando, um celular em /player/:telaId mandava heartbeat, processava
 * remote_cmd (concluindo pedidos no banco) e marcava a TV real offline no
 * pagehide — mesmo exibindo só o PlayerMobileGate.
 */
export default function Player() {
  const { telaId } = useParams<{ telaId: string }>();
  const isMobileViewport = useIsMobileViewport();

  if (isMobileViewport && telaId) {
    return <PlayerMobileGate telaId={telaId} />;
  }
  return <PlayerTv />;
}

function PlayerTv() {
  const { telaId } = useParams<{ telaId: string }>();
  const navigate = useNavigate();

  // ── Estado de infraestrutura ───────────────────────────────────────────────
  const {
    tela, midias, loading, error, revogado, isOnline,
    setTela, setMidias, setRevogado, recarregarTelaEMidias,
  } = usePlayerInfra(telaId);

  // ── Configurações remotas ──────────────────────────────────────────────────
  const [playerConfig, handleUpdateSettings, recarregarConfig] = usePlayerConfig(telaId);

  const {
    videoId: transmissaoVideoId,
    titulo:  transmissaoTitulo,
  } = useTransmissaoLive({
    telaId,
    transmissaoFonteId:         playerConfig.transmissao_fonte_id,
    transmissaoVideoIdOverride: playerConfig.transmissao_video_id_override,
  });

  // ── Slideshow (mídias agendadas) ──────────────────────────────────────────
  const activeMidias = useScheduledMidias(midias);

  // ── Campanhas da plataforma (S2b) ─────────────────────────────────────────
  // Viram objetos no formato Midia: o mesmo agendamento por data, cache de blobs,
  // barra lateral e slideshow das mídias do bar servem para elas.
  const campanhasPlataforma = useCampanhasPlataforma(telaId);
  const campanhasAtivas = useScheduledMidias(campanhasPlataforma.campanhas) as MidiaCampanha[];
  const [voltaSlideshow, setVoltaSlideshow] = useState(0);
  const slideshowMidias = useMemo(
    () => intercalarCampanhas(activeMidias, campanhasAtivas, campanhasPlataforma.reservaPct, voltaSlideshow),
    [activeMidias, campanhasAtivas, campanhasPlataforma.reservaPct, voltaSlideshow],
  );

  // ── Propaganda lateral dinâmica ────────────────────────────────────────────
  // As duas barras (bar e plataforma) se bloqueiam. A do bar é declarada antes e lê
  // um espelho da barra da plataforma (um render de atraso, absorvido pela vez pendente).
  const [campanhaBarraAberta, setCampanhaBarraAberta] = useState(false);
  const { adAtivo, adMidiasRef, handleAdCicloCompleto } = useAdSidebar(
    activeMidias, playerConfig.proporcao_anuncios, { bloqueada: campanhaBarraAberta },
  );

  // ── Logos ──────────────────────────────────────────────────────────────────
  const logoUrl       = useLogo();
  const playerLogoUrl = usePlayerLogos(playerConfig.logos_urls);
  const logoAtiva     = playerLogoUrl ?? logoUrl;

  // ── Refs de execução do Controle Remoto ────────────────────────────────────
  // Locais em Player.tsx (não em usePlayerRemoteState) para quebrar a
  // dependência circular: usePlayerMediaSource e useMomentoEspecial os consomem
  // e são declarados antes de usePlayerRemoteState no corpo de Player().
  // usePlayerRemoteState recebe estas refs como parâmetro e as popula.
  const playerPlayingRef            = useRef(true);
  const playerMutedRef              = useRef(false);
  const playerVolumeRef             = useRef(100);
  const broadcastEstadoPlayerRef    = useRef<() => void>(() => {});
  const locucaoAtivaRef             = useRef(false);

  // ── Notificações ──────────────────────────────────────────────────────────
  const notifToasts = useNotificationToasts();

  // ── Refs de mídia (playlist / transmissão / captura) ─────────────────────
  const ytSlideRef      = useRef<YoutubeSlideHandle>(null);
  const ytLiveRef       = useRef<YoutubeSlideHandle>(null);
  const captureSlideRef = useRef<CaptureSlideHandle>(null);
  const windowCaptureRef = useRef<WindowCaptureHandle>(null);

  // ── Origem de mídia / DSP de áudio ────────────────────────────────────────
  const mediaSource = usePlayerMediaSource({
    playerConfig,
    ytSlideRef, ytLiveRef, captureSlideRef,
    playerPlayingRef, playerMutedRef, playerVolumeRef,
    broadcastEstadoPlayerRef,
    transmissaoVideoId,
  });

  const modoPlaylistJukebox = !!tela?.playlist_id && mediaSource.origemMidia === 'playlist';

  // A coluna de 25% só existe com playlist e fora de captura/transmissão (PlayerContent).
  const barraNaTela = !!tela?.playlist_id && mediaSource.origemMidia !== 'transmissao'
    && mediaSource.origemMidia !== 'captura' && mediaSource.origemMidia !== 'captura_janela';
  const barraCampanha = useAdSidebar(
    campanhasAtivas, campanhasPlataforma.reservaPct, { porAbertura: 1, bloqueada: adAtivo || !barraNaTela },
  );
  useEffect(() => { setCampanhaBarraAberta(barraCampanha.adAtivo); }, [barraCampanha.adAtivo]);
  const [sinalInsercaoFila, setSinalInsercaoFila] = useState(0);

  const engajamentoHud = useTvEngajamento(
    telaId,
    modoPlaylistJukebox && playerConfig.exibir_engajamento_hud,
    sinalInsercaoFila,
  );

  // ── Fila do Jukebox ────────────────────────────────────────────────────────
  // Refs de encaminhamento (mesmo padrão de sendToTelaRef mais
  // abaixo): `jukebox` precisa existir ANTES de `useYoutubePlaylist` (para o
  // argumento pausarPersistencia = !!jukebox.pedidoAtual e para
  // onPlaylistRemoved chamar jukebox.resetarFila()), mas `jukebox` também
  // depende de `playlist.avancarDeck` e `broadcastMusicaAtual` — ambos só
  // existem depois de `useYoutubePlaylist`/`useCallback` mais abaixo no
  // arquivo. As duas funções ficam disponíveis com uma volta de atraso via
  // ref, sincronizada por useEffect logo após suas declarações reais.
  const avancarDeckRef          = useRef<() => number | undefined>(() => undefined);
  const broadcastMusicaAtualRef = useRef<(item: PlaylistItem | FilaJukebox) => void>(() => {});
  const reconciliarFilaRef      = useRef<() => Promise<void>>(async () => {});
  const avancarDeckStable = useCallback(() => avancarDeckRef.current(), []);
  const broadcastMusicaAtualStable = useCallback(
    (item: PlaylistItem | FilaJukebox) => broadcastMusicaAtualRef.current(item),
    [],
  );

  const jukebox = useJukeboxQueue({
    telaId: telaId ?? '',
    empresaId: tela?.empresa_id,
    hasPlaylist: !!tela?.playlist_id,
    sendToTela: (event, payload) => sendToTelaRef.current(event, payload),
    broadcastMusicaAtual: broadcastMusicaAtualStable,
    mostrarPedidoPopup: notifToasts.mostrarPedidoPopup,
    avancarDeck: avancarDeckStable,
    configInvasaoJunina:   playerConfig.config_invasao_junina,
    configIntervaloJunina: playerConfig.config_intervalo_junina,
    origemMidia:  mediaSource.origemMidia,
    audioCaptura: mediaSource.audioCaptura,
  });

  useEffect(() => { reconciliarFilaRef.current = jukebox.reconciliarFila; });

  // ── Modo YouTube ───────────────────────────────────────────────────────────
  const playlist = useYoutubePlaylist(tela?.playlist_id, !!jukebox.pedidoAtual, {
    onPlaylistRemoved: () => {
      jukebox.resetarFila();
      ytSlideRef.current?.pause();
    },
    reproducaoAleatoria: playerConfig.reproducao_aleatoria,
  });

  useEffect(() => { avancarDeckRef.current = playlist.avancarDeck; }, [playlist.avancarDeck]);

  // ── Navegação ──────────────────────────────────────────────────────────────
  const { showControls, handleMouseMove } = useAutoHideControls();

  const handleVoltar = useCallback(() => {
    const destino = tenantHomePath(tela?.empresa_id);
    const exit = document.fullscreenElement ? document.exitFullscreen() : Promise.resolve();
    exit.finally(() => navigate(destino));
  }, [navigate, tela?.empresa_id]);

  // ── Refs de vídeo e slide ─────────────────────────────────────────────────
  const videoRef           = useRef<HTMLVideoElement>(null);

  // ── Gerenciamento de Blob URLs (Memory Manager) ───────────────────────────
  const midiasComCampanhas = useMemo(() => [...midias, ...campanhasPlataforma.campanhas], [midias, campanhasPlataforma.campanhas]);
  const { blobMapRef } = useBlobMemoryManager(midiasComCampanhas);

  // ─────────────────────────────────────────────────────────────────────────
  // Refs de dependência circular
  // Declarados antes de usePlayerSockets para que os callbacks inline possam
  // referenciá-los sem que as funções reais existam ainda no escopo.
  // useEffect os mantém sincronizados após cada render.
  // ─────────────────────────────────────────────────────────────────────────
  const hdmiRefreshRef             = useRef<() => Promise<unknown>>(async () => ({}));
  const hdmiSelectRef              = useRef<(id: string) => void>(() => {});
  const onNetworkRestoredRef     = useRef<() => void>(() => {});
  // sendToTelaRef: disponível imediatamente para onSolicitarMusicaAtual
  const sendToTelaRef = useRef<(event: string, payload: Record<string, unknown>) => void>(() => {});

  const dispararHypeRef = useRef<(opts?: { force?: boolean; manual?: boolean }) => void>(() => {});
  const pdvReceberRef = useRef<(payload: import('@/shared/types').PdvEngajamentoBroadcast) => void>(() => {});
  const pdvTestarRef = useRef<() => void>(() => {});

  // ── Dispatcher de comandos do Controle Remoto ─────────────────────────────
  // Referencia `remoteState` (declarado mais abaixo) via closure — só é
  // chamado em resposta a eventos do socket, depois de remoteState existir.
  const { handleRemoteCmd } = usePlayerRemoteCommands({
    onBroadcastEstadoPlayer: () => remoteState.broadcastEstadoPlayer(),
    onHdmiRefresh: () => hdmiRefreshRef.current(),
    onHdmiSelect: (id) => hdmiSelectRef.current(id),
    onTogglePlay: () => remoteState.togglePlay(),
    getPodeAvancarMidia: () => mediaSource.origemMidia !== 'transmissao',
    onNextMedia: () => jukebox.handleYtEnded(),
    getPlaylistLength: () => playlist.playlistItemsRef.current.length,
    onPrevMedia: (len) => playlist.setYtIndex(i => (i - 1 + len) % len),
    onToggleMute: () => remoteState.toggleMute(),
    onPlayItem: (index) => playlist.setYtIndex(index),
    onDeleteFilaItem: (id) => jukebox.removerPedido(id),
    telaId,
    onToggleQrcode: () => remoteState.setQrVisible(v => !v),
    onToggleRandom: () => remoteState.setIsRandom(r => !r),
    onDisplayFlashMessage: (msg) => remoteState.setFlashMessage(msg),
    onClearFlashMessage: () => remoteState.setFlashMessage(null),
    onSetGanhoCaptura: (ganho) => mediaSource.setGanho(ganho),
    onSetPerfilAudio: (perfil) => mediaSource.setPerfil(perfil),
    onSetOrigemMidia: (origem, audio) => mediaSource.setOrigem(origem, audio),
    onToggleNetworkDebug: () => remoteState.setShowNetworkHud(prev => !prev),
    onReloadPlaylist: () => void playlist.recarregarItens(),
    onHypeMoment: (force) => dispararHypeRef.current({ manual: true, force }),
    onPdvTesteDestaque: () => pdvTestarRef.current(),
    onSetVolume: (volume) => remoteState.setVolume(volume),
  });

  // ── Hub centralizado de canais Supabase Realtime ──────────────────────────
  const { sendToTela } = usePlayerSockets(telaId, {
    hasJukebox: !!tela?.playlist_id,

    onAdminRevoke:   () => setRevogado(true),
    onContaSuspensa: () => { window.location.href = '/bloqueado'; },

    onRemoteCmd: handleRemoteCmd,

    onUpdateSettings: handleUpdateSettings,

    onMidiaInsert: (m) => setMidias(prev =>
      [...prev.filter(x => x.id !== m.id), m].sort((a, b) => a.ordem - b.ordem)),
    onMidiaUpdate: (m) => setMidias(prev => prev.map(x => x.id === m.id ? m : x)),
    onMidiaDelete: (midia) => {
      // Revoga a Blob URL imediatamente — antes do diff de estado — para liberar RAM
      if (midia.url) {
        const blobUrl = blobMapRef.current.get(midia.url);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          blobMapRef.current.delete(midia.url);
        }
        mediaCache.evict([midia.url]);
      }
      setMidias(prev => {
        const next = prev.filter(m => m.id !== midia.id);
        setCurrentIndex(i => Math.min(i, Math.max(0, next.length - 1)));
        return next;
      });
    },

    onTelaUpdate: (t) => setTela(t),

    onNovoJukeboxPedido: (item) => {
      setSinalInsercaoFila(n => n + 1);
      jukebox.adicionarPedido(item);
      notifToasts.enfileirarNotif({
        tipo:      'fila_adicionada',
        titulo:    item.titulo,
        thumbnail: item.thumbnail,
        duracao:   5_000,
        clienteNome:      item.cliente_nome,
        clienteAvatarUrl: item.cliente_avatar_url,
      });
    },

    onFilaJukeboxUpdate: (item) => {
      if (item.status === 'pendente') {
        jukebox.adicionarPedido(item);
      } else if (item.status === 'concluido') {
        jukebox.removerPedido(item.id);
      }
      // 'tocando' é setado pelo próprio Player em handleYtEnded — eco do CDC é ignorado
    },

    onFilaJukeboxDelete: (id) => {
      jukebox.removerPedido(id);
    },

    onMomentoEspecial: (nome, videoUrl) => momento.trigger(nome, videoUrl),

    onSolicitarMusicaAtual: () => {
      const faixa = resolverFaixaAtual(
        jukebox.pedidoAtualRef.current,
        playlist.playlistItemsRef.current[playlist.ytIndexRef.current]
          ?? playlist.playlistItemsRef.current[0],
      );
      const payload = montarMusicaAtualBroadcast(faixa, {
        duracao_segundos: remoteState.musicaDuracaoAtualRef.current,
        iniciada_em: remoteState.musicaIniciadaEmRef.current ?? undefined,
      });
      if (payload) sendToTelaRef.current('musica_atual', payload as unknown as Record<string, unknown>);
    },

    onNetworkRestored: () => onNetworkRestoredRef.current(),

    onCanalRessincronizado: () => {
      // Quedas curtas (auto-reconnect) também perdem broadcasts efêmeros.
      recarregarConfig();
      recarregarTelaEMidias();
      campanhasPlataforma.recarregar();
    },

    onPdvEngajamento: (payload) => pdvReceberRef.current(payload),

    onCampanhasMudaram: () => campanhasPlataforma.recarregar(),

    onSetVolume: (volume) => remoteState.setVolume(volume),
  });

  useEffect(() => { sendToTelaRef.current = sendToTela; }, [sendToTela]);

  // ── Estado de execução do Controle Remoto + broadcasts ────────────────────
  // Chamado depois de usePlayerSockets (consome `sendToTela`) e de
  // jukebox/playlist (consome seus refs). Recebe as 4 refs compartilhadas
  // locais para quebrar a dependência circular com mediaSource/momento.
  const remoteState = usePlayerRemoteState({
    tela,
    sendToTela,
    ytSlideRef, ytLiveRef, captureSlideRef, windowCaptureRef, videoRef,
    volumePadrao:        playerConfig.volume_padrao,
    qrcodeJukebox:       playerConfig.qrcode_jukebox,
    reproducaoAleatoria: playerConfig.reproducao_aleatoria,
    pedidoAtualRef:      jukebox.pedidoAtualRef,
    playlistItemsRef:    playlist.playlistItemsRef,
    ytIndexRef:          playlist.ytIndexRef,
    playerPlayingRef, playerMutedRef, playerVolumeRef, broadcastEstadoPlayerRef,
    locucaoAtivaRef,
  });

  // Encaminha broadcastMusicaAtual para o ref estável consumido por useJukeboxQueue
  useEffect(() => { broadcastMusicaAtualRef.current = remoteState.broadcastMusicaAtual; }, [remoteState.broadcastMusicaAtual]);

  const {
    dispositivos: hdmiDispositivos,
    deviceIdAtivo: hdmiDeviceIdAtivo,
    refreshDispositivos,
    selecionarDispositivo,
  } = useHdmiCaptura(sendToTela);

  useEffect(() => {
    hdmiRefreshRef.current = refreshDispositivos;
    hdmiSelectRef.current  = selecionarDispositivo;
  }, [refreshDispositivos, selecionarDispositivo]);

  useEffect(() => {
    if (!telaId) return;
    void refreshDispositivos();
  }, [telaId, refreshDispositivos]);

  useEffect(() => {
    if (mediaSource.origemMidia === 'captura') void refreshDispositivos();
  }, [mediaSource.origemMidia, refreshDispositivos]);

  // ── Recuperação pós-queda de rede (≥3 min) ────────────────────────────────
  // Chamado por usePlayerSockets quando o canal SUBSCRIBED confirma que o WS
  // foi recriado pelo Watchdog após timeout. Força o player de mídia a sair do
  // estado de spinner infinito causado por XHRs internos quebrados durante a queda.
  const onNetworkRestored = useCallback(() => {
    console.info('[Player] Rede restaurada após queda — forçando retomada de mídia');
    // Catch-up de config/tela/mídias fica em onCanalRessincronizado, que também
    // dispara neste SUBSCRIBED do watchdog.
    if (tela?.playlist_id) {
      void reconciliarFilaRef.current();
      ytSlideRef.current?.recoverFromNetworkDrop();
      if (mediaSource.origemMidia === 'transmissao') {
        ytLiveRef.current?.recoverFromNetworkDrop();
      }
    } else {
      // Modo slideshow de mídias locais: força re-buffering do vídeo em exibição.
      // Blob URLs relêem do cache instantaneamente; o play() é não-destrutivo se
      // o vídeo já estiver em condições de reproduzir.
      const video = videoRef.current;
      if (video && video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
        video.load();
        video.play().catch(() => {});
      }
    }
  }, [tela?.playlist_id, mediaSource.origemMidia]);

  useEffect(() => { onNetworkRestoredRef.current = onNetworkRestored; }, [onNetworkRestored]);

  // ── Votação automática pré-jogo (por tela) ───────────────────────────────
  useVotacaoAutomatica(telaId ?? '');

  // ── Partida ativa (para Termômetro da Torcida) ───────────────────────────
  const { partida: partidaAtiva, jogo: partidaJogo } = useFutebolRealtime(telaId ?? '');

  const pedidoAnteriorRef  = useRef<FilaJukebox | null>(null);
  const [ultimoFimPedidoCliente, setUltimoFimPedidoCliente] = useState<number | null>(null);
  useEffect(() => {
    if (pedidoAnteriorRef.current && !jukebox.pedidoAtual) {
      setUltimoFimPedidoCliente(Date.now());
    }
    pedidoAnteriorRef.current = jukebox.pedidoAtual;
  }, [jukebox.pedidoAtual]);

  const hypeMoment = useHypeMoment({
    habilitado:             modoPlaylistJukebox,
    config:                 playerConfig,
    pedidoAtual:            jukebox.pedidoAtual,
    showPedidoPopup:        !!notifToasts.showPedidoPopup,
    flashAtivo:             remoteState.flashMessage != null,
    partidaAtiva,
    ultimoFimPedidoCliente,
  });

  useEffect(() => {
    dispararHypeRef.current = hypeMoment.dispararHype;
  }, [hypeMoment.dispararHype]);

  // ── RAIO-X da música ──────────────────────────────────────────────────────
  // A faixa atual vem do pedido do cliente quando há um, senão da playlist.
  const faixaRaioX = useMemo(() => {
    if (jukebox.pedidoAtual) {
      return {
        videoId: jukebox.pedidoAtual.youtube_video_id,
        titulo:  jukebox.pedidoAtual.titulo,
        canal:   null as string | null,
        duracao: jukebox.pedidoAtual.duracao_segundos ?? null,
      };
    }
    const item = playlist.playlistItems[playlist.ytIndex];
    if (!item) return null;
    return {
      videoId: item.video_id,
      titulo:  item.titulo,
      canal:   item.canal,
      duracao: item.duracao_segundos,
    };
  }, [jukebox.pedidoAtual, playlist.playlistItems, playlist.ytIndex]);

  const raioX = useRaioXMusica({
    habilitado:      modoPlaylistJukebox && playerConfig.exibir_raiox_musica,
    videoId:         faixaRaioX?.videoId ?? null,
    titulo:          faixaRaioX?.titulo  ?? null,
    canal:           faixaRaioX?.canal   ?? null,
    duracaoSegundos: faixaRaioX?.duracao ?? null,
    bloqueado:       !!notifToasts.showPedidoPopup,
  });

  // ── Identidade Sonora: locução sobreposta a cada N faixas ────────────────
  const identidadeSonora = useIdentidadeSonora({
    telaId,
    habilitado:      modoPlaylistJukebox,
    videoIdAtual:    faixaRaioX?.videoId ?? null,
    ytSlideRef,
    playerVolumeRef,
    playerMutedRef,
    locucaoAtivaRef,
  });

  // ── Persistência de chaves de evento/intervalo de futebol ────────────────
  const {
    lastEventKeyRef:    lastFutebolEventKeyRef,
    lastIntervalKeyRef,
    onEventoProcessado: onFutebolEventoProcessado,
    onIntervalProcessado,
  } = useFutebolEventPersistence(partidaAtiva?.fixture_id ?? null);

  // ── Heartbeat ──────────────────────────────────────────────────────────
  useHeartbeat({
    telaId:    telaId ?? '',
    enabled:   !!telaId && !loading && !error && !revogado,
    telaNome:  tela?.nome,
    empresaId: tela?.empresa_id,
  });

  // ── Efeitos sonoros das notificações de futebol ───────────────────────────
  useFutebolSounds(playerVolumeRef, playerMutedRef);

  const handleLiveEnded = useCallback(() => {
    console.info('[Player] Transmissão ao vivo encerrada (ENDED)');
  }, []);

  const handleLiveVideoError = useCallback((code: number) => {
    console.warn(`[Player] Erro YouTube live ${code} — transmissão`);
  }, []);

  // ── Erro de vídeo YouTube (Jukebox) ───────────────────────────────────────
  const handleYtVideoError = useYoutubeErroEmbed({
    telaId,
    pedidoAtualRef: jukebox.pedidoAtualRef,
    playlistItemsRef: playlist.playlistItemsRef,
    ytIndexRef: playlist.ytIndexRef,
  });

  // ── Momentos Especiais (Parabéns) ─────────────────────────────────────────
  const momento = useMomentoEspecial({
    ytSlideRef, ytLiveRef, captureSlideRef, videoRef,
    playerVolumeRef, playerPlayingRef, playerMutedRef,
    origemMidia: mediaSource.origemMidia,
    audioCaptura: mediaSource.audioCaptura,
    sendToTela, ytUnlocked: mediaSource.ytUnlocked,
    audioFonte:     playerConfig.momento_especial_audio_fonte,
    audioUrl:       playerConfig.momento_especial_audio_url,
    audioInicioSeg: playerConfig.momento_especial_audio_inicio_seg,
    audioFimSeg:    playerConfig.momento_especial_audio_fim_seg,
  });

  // ── PDV Engajamento (Saipos): card de destaque + termômetro do chopp ──────
  // Vivem na coluna de vídeo (PlayerContent) e não entram em notifSistemaAtiva:
  // só cedem para o que toma o centro/topo da tela (popup de pedido, gol,
  // Momento Especial/aniversário).
  const pdv = usePdvEngajamento({
    telaId: telaId ?? '',
    habilitado: modoPlaylistJukebox && playerConfig.exibir_pdv_engajamento,
    bloqueado: !!notifToasts.showPedidoPopup || momento.celebracaoAtiva || isGolOverlayAtivo(partidaAtiva),
  });
  useEffect(() => { pdvReceberRef.current = pdv.receber; pdvTestarRef.current = pdv.testarDestaque; }, [pdv.receber, pdv.testarDestaque]);

  const notifSistemaAtiva = !!(
    notifToasts.showPedidoPopup
    || notifToasts.nowPlaying
    || notifToasts.notifFilaAtiva
    || remoteState.flashMessage != null
    || hypeMoment.hypeQrDestaque
    || raioX.visivel
  );

  // ── Resultado da eleição (TSE): faixa central inferior ────────────────────
  // Nunca some: só o toggle da tela a esconde (vale em playlist, transmissão, captura e slideshow).
  // Não entra em notifSistemaAtiva; sobe junto com o logo quando o RAIO-X aparece (PlayerContent).
  const eleicaoSnapshot = useResultadoEleicao({
    habilitado: playerConfig.exibir_resultado_eleicao,
  });
  // O ticker da fila divide o rodapé com a faixa: enquanto há candidatos na tela, ele cede.
  const eleicaoOcupaRodape = eleicaoSnapshot != null && eleicaoSnapshot.candidatos.length > 0;

  const mostrarFilaProxima = useMemo(() => (
    modoPlaylistJukebox
    && playerConfig.exibir_fila_proxima_tv
    && jukebox.filaEspera.some(i => !i.is_invasao_tematica)
    && !notifSistemaAtiva
    && !eleicaoOcupaRodape
  ), [
    modoPlaylistJukebox,
    playerConfig.exibir_fila_proxima_tv,
    jukebox.filaEspera,
    notifSistemaAtiva,
    eleicaoOcupaRodape,
  ]);

  // ── Broadcast ao trocar item da playlist ─────────────────────────────────
  useEffect(() => {
    if (!tela?.playlist_id || playlist.playlistItems.length === 0 || jukebox.pedidoAtualRef.current) return;
    const item = playlist.playlistItems[playlist.ytIndex];
    if (!item) return;
    remoteState.broadcastMusicaAtual(item);
    if (playerConfig.modal_agora_tocando) notifToasts.mostrarNowPlaying({ titulo: item.titulo, thumbnail: item.thumbnail });
  }, [playlist.ytIndex, playlist.playlistItems, tela?.playlist_id, remoteState.broadcastMusicaAtual, playerConfig.modal_agora_tocando, notifToasts.mostrarNowPlaying]);

  // ── Slideshow (avanço automático + Watchdog) ──────────────────────────────
  const {
    currentIndex, setCurrentIndex,
  } = useSlideshowAdvance({
    activeMidias: slideshowMidias,
    momentoEspecial: momento.momentoEspecial,
    hasPlaylist: !!tela?.playlist_id,
    videoRef,
    telaId: telaId ?? '',
  });

  // Volta completa do slideshow: avança o revezamento das campanhas que não couberam.
  const indiceAnteriorRef = useRef(0);
  useEffect(() => {
    if (currentIndex === 0 && indiceAnteriorRef.current > 0) setVoltaSlideshow(v => v + 1);
    indiceAnteriorRef.current = currentIndex;
  }, [currentIndex]);

  // ── Contagem de exibições das campanhas ──────────────────────────────────
  // Só conta o que está de fato na tela: barra da plataforma visível, ou item de
  // campanha no slideshow (TV sem playlist). Momento Especial e aba oculta não contam.
  const slideshowNaTela = !tela?.playlist_id && mediaSource.origemMidia === 'playlist';
  const itemSlideshow = slideshowNaTela ? slideshowMidias[currentIndex] : undefined;
  // !adAtivo: com as duas ativas (corrida rara de temporizadores) o PlayerContent mostra a do bar.
  const campanhaEmTela: MidiaCampanha | null = barraCampanha.adAtivo && !adAtivo && barraNaTela
    ? ((barraCampanha.adMidiasRef.current[0] as MidiaCampanha | undefined) ?? null)
    : (ehCampanha(itemSlideshow) ? itemSlideshow : null);
  useRegistrarExibicoes({ telaId, itemAtual: campanhaEmTela, visivel: !momento.momentoEspecial });

  function handleRequestFullscreen() {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Renderização
  //
  // PlayerGuard intercepta loading / revogado / error antes de renderizar o player.
  // O conteúdo interno recebe estado garantidamente limpo.
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <PlayerGuard loading={loading} error={error} revogado={revogado} telaId={telaId}>
      {tela && telaId && (
        <PlayerContent
          tela={tela}
          telaId={telaId}
          midias={midias}
          activeMidias={slideshowMidias}
          currentIndex={currentIndex}
          origemMidia={mediaSource.origemMidia}
          audioCaptura={mediaSource.audioCaptura}
          playlistItems={playlist.playlistItems}
          ytIndex={playlist.ytIndex}
          playNonce={playlist.playNonce}
          pedidoAtual={jukebox.pedidoAtual}
          playerConfig={playerConfig}
          qrVisible={remoteState.qrVisible}
          hypeQrDestaque={hypeMoment.hypeQrDestaque}
          hypeTexto={hypeMoment.hypeTexto}
          flashMessage={remoteState.flashMessage}
          nowPlaying={notifToasts.nowPlaying}
          nowPlayingSaindo={notifToasts.nowPlayingSaindo}
          showPedidoPopup={notifToasts.showPedidoPopup}
          pedidoPopupSaindo={notifToasts.pedidoPopupSaindo}
          notifFilaAtiva={notifToasts.notifFilaAtiva}
          raioX={raioX.dados}
          raioXVisivel={raioX.visivel}
          pdvDestaque={pdv.overlay?.tipo === 'destaque' ? pdv.overlay.itens : null}
          pdvTermometro={pdv.snapshot.ativo ? {
            hypeHoje:      pdv.snapshot.contadores.hype_hoje,
            proximoMarco:  pdv.snapshot.contadores.proximo_marco,
            hypeRotulo:    pdv.snapshot.contadores.hype_rotulo,
            transbordando: pdv.overlay?.tipo === 'hype_marco' ? { marco: pdv.overlay.marco, total: pdv.overlay.total } : null,
          } : null}
          eleicao={eleicaoSnapshot}
          isOnline={isOnline}
          showControls={showControls}
          logoAtiva={logoAtiva}
          adAtivo={adAtivo}
          adMidiasRef={adMidiasRef}
          blobMapRef={blobMapRef}
          partidaAtiva={partidaAtiva}
          ytUnlocked={mediaSource.ytUnlocked}
          ytSlideRef={ytSlideRef}
          ytLiveRef={ytLiveRef}
          transmissaoVideoId={transmissaoVideoId}
          transmissaoTitulo={transmissaoTitulo}
          captureSlideRef={captureSlideRef}
          windowCaptureRef={windowCaptureRef}
          hdmiDispositivos={hdmiDispositivos}
          hdmiDeviceIdAtivo={hdmiDeviceIdAtivo}
          onSelecionarHdmi={selecionarDispositivo}
          videoRef={videoRef}
          playerVolumeRef={playerVolumeRef}
          playerMutedRef={playerMutedRef}
          ganhoCaptura={mediaSource.ganhoCaptura}
          perfilAudio={mediaSource.perfilAudio}
          handleRequestFullscreen={handleRequestFullscreen}
          handleMouseMove={handleMouseMove}
          handleVoltar={handleVoltar}
          handleYtEnded={jukebox.handleYtEnded}
          handleLiveEnded={handleLiveEnded}
          handleYtVideoError={handleYtVideoError}
          handleLiveVideoError={handleLiveVideoError}
          handleAdCicloCompleto={handleAdCicloCompleto}
          campanhaBarraAtiva={barraCampanha.adAtivo}
          campanhaBarraMidiasRef={barraCampanha.adMidiasRef}
          handleCampanhaCicloCompleto={barraCampanha.handleAdCicloCompleto}
          onYtUnlock={() => mediaSource.setYtUnlocked(true)}
          onJanelaStreamActiveChange={mediaSource.setJanelaStreamAtivo}
        />
      )}
      <PlayerOverlays
        telaId={telaId}
        playerConfig={playerConfig}
        origemMidia={mediaSource.origemMidia}
        ytUnlocked={mediaSource.ytUnlocked}
        momento={momento}
        identidadeSonora={identidadeSonora}
        partidaAtiva={partidaAtiva}
        partidaJogo={partidaJogo}
        lastFutebolEventKey={lastFutebolEventKeyRef.current}
        lastIntervalKey={lastIntervalKeyRef.current}
        onFutebolEventoProcessado={onFutebolEventoProcessado}
        onIntervalProcessado={onIntervalProcessado}
        modoPlaylistJukebox={modoPlaylistJukebox}
        engajamentoHud={engajamentoHud}
        pedidoAtual={jukebox.pedidoAtual}
        showPedidoPopup={!!notifToasts.showPedidoPopup}
        mostrarFilaProxima={mostrarFilaProxima}
        raioXVisivel={raioX.visivel}
        filaEspera={jukebox.filaEspera}
        isInvasaoAtiva={jukebox.isInvasaoAtiva}
        hypeQrDestaque={hypeMoment.hypeQrDestaque}
        showNetworkHud={remoteState.showNetworkHud}
        isOnline={isOnline}
      />
    </PlayerGuard>
  );

}
